import React, { useRef, useEffect, useState, ReactNode } from "react";
import cn from "classnames";
import ClearIcon from "@mui/icons-material/Clear";
import { useSearchParams } from "react-router-dom";

import Button from "Components/UI/Button/Button";
import { changeToParams } from "Shared/Utils/search-params";
import { useDebounce } from "Shared/Hook/use-debounce";

import style from "./ModalOptions.module.scss";
import { DownArrowIcon, DropdowSnIcon } from "Assets";

type DefaultOptionType = {
  id: string;
  name: string;
  [label: string]: string;
};

type ModalOptionsProps<T = DefaultOptionType> = {
  title: string;
  type: string;
  options: T[];
  getOptionValue?: (option: T) => string;
  getOptionLabel?: (option: T) => string;
  fetchOptions: (search: string) => void;
  node?: ReactNode;
  label?: string;
  disabled?: boolean;
  zIndex?: boolean;
};

function ModalOptions<T = DefaultOptionType>({
  title,
  type,
  options = [],
  label,
  zIndex = false,
  fetchOptions,
  getOptionLabel = (option) =>
    label
      ? (option as DefaultOptionType)?.[label]
      : (option as DefaultOptionType)?.name,
  getOptionValue = (option) => (option as DefaultOptionType)?.id,
  node,
  disabled = false,
}: ModalOptionsProps<T>) {
  const [currentText, setCurrentText] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [openModal, setOpenModal] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string>("");
  const [tempSelectedGroup, setTempSelectedGroup] = useState<string>("");
  const modalRef = useRef<HTMLDivElement>(null);
  const selectedGroupFromLocal = localStorage.getItem(type) || "";

  const toggleModal = () => disabled === false && setOpenModal(!openModal);
  const debouncedCurrentText = useDebounce(currentText, 500);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setOpenModal(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchOptions(debouncedCurrentText);
  }, [debouncedCurrentText]);

  const onSearchChange = () => {
    setSelectedOptions(tempSelectedGroup);
    setSearchParams(changeToParams(type, selectedOptions, searchParams));
    setOpenModal(false);
    localStorage.setItem(type, tempSelectedGroup);
  };

  const handleSelectType = (el: T) => {
    setSelectedOptions(getOptionValue(el));
    setTempSelectedGroup(getOptionLabel(el));
    setCurrentText("");
  };

  const clearInput = () => {
    setCurrentText("");
    setTempSelectedGroup("");
    setSelectedOptions("");
    searchParams.delete(type);
    setSearchParams(searchParams);
  };

  useEffect(() => {
    if (!searchParams.has(type)) {
      localStorage.removeItem(type);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!searchParams.has(type)) {
      localStorage.removeItem(type);
      setCurrentText("");
      setTempSelectedGroup("");
      setSelectedOptions("");
    }
  }, [searchParams]);

  return (
    <div
      key={title}
      className={cn(
        style.blockFiltering,
        disabled && style.blockFiltering_disabled,
        zIndex && style.blockFiltering__zIndex
      )}
      title={disabled ? "Choose a form to select items." : ""}
    >
      {node ? (
        <div onClick={toggleModal}>{node}</div>
      ) : (
        <div
          className={cn(
            style.blockFiltering__blockText,
            searchParams.has(type)
              ? style.blockFiltering__blockText_selected
              : ""
          )}
          onClick={toggleModal}
        >
          <span className={style.blockFiltering__blockText__itemTextSearch}>
            {node ? node : title}
          </span>
          <DropdowSnIcon className={cn(style.icon, style.iconDefault)} />
          <DownArrowIcon className={cn(style.icon, style.iconLight)} />
        </div>
      )}

      {openModal && (
        <div ref={modalRef} className={style.blockFiltering__trueModal}>
          <div className={style.blockFiltering__trueModal__inModal}>
            <div
              className={style.blockFiltering__trueModal__inModal__paddingModal}
            >
              <input
                disabled={disabled || tempSelectedGroup.trim() !== ""}
                className={
                  style.blockFiltering__trueModal__inModal__paddingModal__inputClasses
                }
                placeholder="Select Item(s)"
                value={
                  currentText || tempSelectedGroup || selectedGroupFromLocal
                }
                onChange={(event) => setCurrentText(event.target.value)}
              />
              <ClearIcon
                onClick={clearInput}
                className={
                  style.blockFiltering__trueModal__inModal__paddingModal__clearIcon
                }
              />
              <div
                className={
                  style.blockFiltering__trueModal__inModal__paddingModal__scrollbar
                }
              >
                {options.map((el, index) => (
                  <li
                    onClick={() => handleSelectType(el)}
                    key={index}
                    className={cn(
                      style.blockFiltering__trueModal__inModal__paddingModal__listItem,
                      selectedOptions === getOptionValue(el)
                        ? style.activeItem
                        : ""
                    )}
                  >
                    {getOptionLabel(el)}
                  </li>
                ))}
              </div>
            </div>
            <div className={style.blockFiltering__trueModal__inModal__lineBox}>
              <Button color="secondary" onClick={toggleModal}>
                Cancel
              </Button>
              <Button onClick={onSearchChange}>Apply</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ModalOptions;
